import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { useLocation, useNavigate } from "react-router-dom";
import { openModal } from "@store/slices/modalSlice";
import AssignTranslators from "@modals/AssignTranslators";
import { fetchAssignedFileDetails, assignFileToValidatorAdmin, fetchSentenceTableData, fetchInProgressFiles } from "@store/thunks/translationThunk";
import { setFileMeta } from "@store/slices/fileMetaSlice";
import { setFileStatus } from "@store/slices/translatorAdminSlice";
import { formatFileSize, getStatusStyle, restrictSpecialCharacters } from "@utils/constants";
import { toast } from "react-toastify";
import { setFileAndLanguage } from "@/store/slices/fileLanguageSlice";

const FileDetailTableTranslatorAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [reloadTrigger, setReloadTrigger] = useState(0); // Updated to number for increment
  const translatorAdminId = useSelector((store) => store.auth?.user?.userId) || 2;
  const role = useSelector((store) => store.auth?.user?.role);
  const [openLangPopupId, setOpenLangPopupId] = useState(null);
  const [fileLanguages, setFileLanguages] = useState({});
  const { assignedFiles, selectedAssignedFolderId } = useSelector(
    (store) => store.translatorAdmin
  );
  const assignedFileArray = assignedFiles[selectedAssignedFolderId] || [];
  const [file, setFile] = useState({});
  const [previousFileStatuses, setPreviousFileStatuses] = useState({}); // Track previous statuses

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true); // 👈 track if more pages exist
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  // Function to fetch files
  const fetchFiles = async () => {
    if (selectedAssignedFolderId) {
      setIsLoading(true);
      try {
        const res = await dispatch(fetchInProgressFiles(selectedAssignedFolderId, searchQuery, pageIndex, pageSize));
        if (Array.isArray(res)) {
          if (res.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Detect status changes
  useEffect(() => {
    if (assignedFileArray && assignedFileArray.length > 0) {
      const currentStatuses = assignedFileArray.reduce((acc, file) => {
        acc[file.fileId] = file.fileStatus;
        return acc;
      }, {});

      // Compare with previous statuses
      const hasStatusChanged = Object.keys(currentStatuses).some(
        (fileId) => currentStatuses[fileId] !== previousFileStatuses[fileId]
      );

      if (hasStatusChanged) {
        console.log("[FileDetailTableTranslatorAdmin] Status change detected, refetching files");
        fetchFiles();
      }

      // Update previous statuses
      setPreviousFileStatuses(currentStatuses);
    }
  }, [assignedFileArray]);

  useEffect(() => {
    if (selectedAssignedFolderId) {
      fetchFiles();
    } else {
      navigate("/translatorAdmin/folders");
    }
  }, [dispatch, searchQuery, selectedAssignedFolderId, pageIndex, pageSize, navigate, reloadTrigger]);

  const handleAssignClick = ({ row }) => {
    dispatch(setFileMeta({ fileId: row.fileId, fileName: row.fileDetailsString }));
    setFile({ id: row.fileId, name: row.fileDetailsString });
    dispatch(openModal("assignTranslator"));
  };

  const fetchLanguages = async ({ row }) => {
    const fileId = row.fileId;
    try {
      console.log(`Fetching data for fileId=${fileId}, translatorAdminId=${translatorAdminId}`);
      const resultAction = await dispatch(fetchAssignedFileDetails({ fileId, translatorUserId: translatorAdminId }));
      if (fetchAssignedFileDetails.fulfilled.match(resultAction)) {
        const result = resultAction.payload;
        setFileLanguages((prev) => ({ ...prev, [fileId]: result }));
        setOpenLangPopupId(fileId);
      } else {
        console.error("Failed to fetch file details:", resultAction.error);
        toast.error(
          <div className="p-3 font-serif text-sm font-medium text-gray-800">
            Error: Failed to fetch language data
          </div>,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            className: "rounded-lg shadow-md border border-red-200",
          }
        );
      }
    } catch (err) {
      console.error("Error fetching languages:", err);
      toast.error(
        <div className="p-3 font-serif text-sm font-medium text-gray-800">
          Error: Failed to fetch language data
        </div>,
        {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
          className: "rounded-lg shadow-md border border-red-200",
        }
      );
    }
  };

  const fileColumns = [
    {
      header: "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      header: "File Name",
      accessorKey: "fileName",
      cell: ({ row }) => (
        <button
          className="text-[#ec7026] hover:text-[#d85d1a] focus:underline font-['Semantic/Strong']"
          onClick={() => fetchLanguages({ row: row.original })}
        >
          {row.original.fileName || "N/A"}
        </button>
      ),
    },
    {
      header: "File Size",
      accessorKey: "fileSizeBytes",
      cell: ({ row }) => <span>{formatFileSize(row.original.fileSizeBytes)}</span>,
    },
    {
      header: "Total Sentences",
      accessorKey: "totalSentences",
    },
    {
      header: "Status",
      accessorKey: "fileStatus",
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.fileStatus);
        return (
          <span className={`inline-flex items-center text-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
            {style.label || "N/A"}
          </span>
        );
      },
    },
    {
      id: "uploadedAt",
      header: () => "Uploaded At",
      accessorKey: "uploadedAt",
      cell: ({ row }) => {
        const deadline = row.original?.uploadedAt || row.uploadedAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
      cellClassName: "text-center",
    },
    {
      header: "Assigned To",
      accessorKey: "assignedVendor",
      cell: ({ row }) => (
        <div className="flex justify-center">
          <button
            className={`inline-flex items-center px-3 py-1.5 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white ${row.original.assignedVendor == null ? "bg-[#ec7026] hover:bg-[#d85d1a]" : "bg-green-500 hover:bg-green-600"
              } focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200`}
            onClick={(e) => {
              e.stopPropagation();
              handleAssignClick({ row: row.original });
            }}
          >
            {row.original.assignedVendor == null ? "Assign" : "Assigned"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <AssignTranslators files={file} />
      <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <GenericTable
              data={assignedFileArray}
              columns={fileColumns}
              showPagination={true}
              manualPagination={true}
              pageIndex={pageIndex}
              pageSize={pageSize}
              hasNextPage={hasNextPage}
              onPaginationChange={({ pageIndex, pageSize }) => {
                setPageIndex(pageIndex);
                setPageSize(pageSize);
              }}
              emptyMessage="No files available"
            />
          </div>
        </div>
      </div>
      {openLangPopupId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-200">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-[90vw] max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
              <h2 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                Assigned Languages
              </h2>
              <p className="text-sm text-gray-500 font-['Semantic/Strong'] mt-1">
                Details of languages assigned for the selected file
              </p>
            </div>
            <div className="p-4 sm:p-6 lg:p-8">
              {fileLanguages[openLangPopupId]?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr className="text-gray-900 text-sm font-['Semantic/Strong']">
                        <th className="border border-gray-200 px-4 py-2 text-left">Translator</th>
                        <th className="border border-gray-200 px-4 py-2 text-left">Source Language</th>
                        <th className="border border-gray-200 px-4 py-2 text-left">Target Language</th>
                        <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
                        <th className="border border-gray-200 px-4 py-2 text-left">Progress</th>
                        <th className="border border-gray-200 px-4 py-2 text-left">Sentence Statistics</th>
                        <th className="border border-gray-200 px-4 py-2 text-center">Open File</th>
                        <th className="border border-gray-200 px-4 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 text-sm font-['Semantic/Strong']">
                      {fileLanguages[openLangPopupId].map((lang, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                          <td className="border border-gray-200 px-4 py-2">{lang.translatorUserName || "N/A"}</td>
                          <td className="border border-gray-200 px-4 py-2">{lang.sourceName || "N/A"}</td>
                          <td className="border border-gray-200 px-4 py-2">{lang.targetName || "N/A"}</td>
                          <td className="border border-gray-200 px-4 py-2">
                            <span className={`px-2 py-1 rounded-full ${getStatusStyle(lang.taStatus)}`}>
                              {lang.taStatus || "N/A"}
                            </span>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-[#ec7026] h-2 rounded-full"
                                  style={{ width: `${lang.percentageCompleted}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">{lang.percentageCompleted}%</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {lang.translatedSentenceCount}/{lang.totalSentences} sentences
                            </div>
                          </td>
                          <td className="border border-gray-200 px-4 py-2">
                            <div className="text-sm">
                              <div className="flex justify-between mb-1">
                                <span className="text-gray-600">Total:</span>
                                <span>{lang.totalSentences}</span>
                              </div>
                              <div className="flex justify-between mb-1">
                                <span className="text-green-600">Translated:</span>
                                <span className="text-green-600">{lang.translatedSentenceCount}</span>
                              </div>
                              <div className="flex justify-between mb-1">
                                <span className="text-red-600">Rejected:</span>
                                <span className="text-red-600">{lang.rejectedSentenceCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#ec7026]">Completed:</span>
                                <span className="text-[#ec7026]">{lang.percentageCompleted}%</span>
                              </div>
                            </div>
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-center">
                            <button
                              className="inline-flex items-center px-3 py-1.5 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                              onClick={async () => {
                                dispatch(setFileStatus(lang.taStatus));
                                dispatch(setFileMeta({
                                  fileName: lang.fileName
                                }));
                                dispatch(setFileAndLanguage({
                                  targetLanguageName: lang.targetName,
                                }))
                                navigate(
                                  `/translatorAdmin/folders/files/sentences/${lang.fileId}/${lang.sourceId}/${lang.targetId}?status=${lang.taStatus}`
                                );
                              }}
                            >
                              Open
                            </button>
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-center">
                            <button
                              className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-sm font-['Semantic/Strong'] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200 ${lang.taStatus === "COMPLETED"
                                ? "border-[#ec7026] text-white bg-[#ec7026] hover:bg-[#d85d1a]"
                                : "border-gray-300 text-gray-500 bg-gray-200 cursor-not-allowed"
                                }`}
                              onClick={async () => {
                                if (lang.taStatus === "COMPLETED") {
                                  let res;
                                  try {
                                    console.log("[FileDetailTableTranslatorAdmin] Sending file to Validator Admin:", lang.fileId);
                                    res = await dispatch(
                                      assignFileToValidatorAdmin({
                                        fileId: lang.fileId,
                                        sourceLanguageId: lang.sourceId,
                                        targetLanguageId: lang.targetId,
                                      })
                                    );
                                    setReloadTrigger((prev) => prev + 1);
                                    toast.success(
                                      <div className="p-3 font-serif text-sm font-medium text-gray-800">
                                        File sent to Validator Admin successfully!
                                      </div>,
                                      {
                                        position: "top-right",
                                        autoClose: 3000,
                                        theme: "colored",
                                        className: "rounded-lg shadow-md border border-green-200",
                                      }
                                    );
                                  } catch (err) {
                                    console.error("[FileDetailTableTranslatorAdmin] Error sending file to Validator Admin:", err);
                                    toast.error(
                                      <div className="p-3 font-serif text-sm font-medium text-gray-800">
                                        {res.message}
                                      </div>,
                                      {
                                        position: "top-right",
                                        autoClose: 3000,
                                        theme: "colored",
                                        className: "rounded-lg shadow-md border border-red-200",
                                      }
                                    );
                                  }
                                }
                              }}
                              disabled={lang.taStatus !== "COMPLETED"}
                            >
                              {lang.taStatus === "COMPLETED" ? "Send to Validator Admin" : lang.taStatus === 'SENT_TO_VA' ? 'Sent to Validator Admin' : "No action"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 font-['Semantic/Strong']">No languages assigned</p>
              )}
              <div className="px-4 sm:px-6 lg:px-8 py-4 text-right">
                <button
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                  onClick={() => setOpenLangPopupId(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileDetailTableTranslatorAdmin;